import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { Grid } from '@mui/material';
import LoadingIndicator from 'components/LoadingIndicator';
import Navbar from 'components/Navbar';
import { useUser } from 'contexts/userContext';
import {
  GET_DEPARTMENTS_QUERY,
  GetUserDepartmentResponse,
  GetUserDepartmentVariables,
} from 'graphql/query/user';
import { downloadBinaryData } from 'utils/admin';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { getSelectedObjectiveGoalsYear } from 'utils/common';

const HOD_REPORT_QUERY = gql`
  query DownloadReportByHodDepartments($hodId: ID!, $department: String!, $year: Int) {
    downloadReportByHodDepartments(hodId: $hodId, department: $department, year: $year) {
      file
      fileName
    }
  }
`;

type GetHodReportResponse = {
  downloadReportByHodDepartments: {
    fileName: string;
    file: string;
  };
};

type GetHodReportVariables = {
  hodId: string;
  department: string;
  year: number;
};

const HODReportsPage = () => {
  const { user } = useUser();

  const { data: departments, loading: loadingDepartments } = useQuery<
    GetUserDepartmentResponse,
    GetUserDepartmentVariables
  >(GET_DEPARTMENTS_QUERY);

  const [downloadDeptReport, { loading: downloadingDeptReport }] = useLazyQuery<
    GetHodReportResponse,
    GetHodReportVariables
  >(HOD_REPORT_QUERY, {
    fetchPolicy: 'network-only',
  });

  const handleDeptReportDownload = (dept: string) => {
    downloadDeptReport({
      variables: {
        hodId: user?._id as string,
        department: dept,
        year: getSelectedObjectiveGoalsYear(),
      },
      onCompleted: res => {
        downloadBinaryData({
          filename: res.downloadReportByHodDepartments.fileName,
          content: res.downloadReportByHodDepartments.file,
        });
      },
    });
  };

  return (
    <Navbar title={`Department Reports - ${getSelectedObjectiveGoalsYear()}`}>
      {loadingDepartments ? (
        <LoadingIndicator />
      ) : (
        <Grid
          container
          rowGap={2}
          columnSpacing={1.5}
          alignItems="center"
          justifyContent="center"
          mt={4}
        >
          {(departments?.getUserDepartment ?? []).map(dept => (
            <Grid item>
              <LoadingButton
                disabled={downloadingDeptReport}
                key={dept}
                variant="contained"
                onClick={() => handleDeptReportDownload(dept)}
                startIcon={<FileDownloadIcon />}
              >
                {dept}
              </LoadingButton>
            </Grid>
          ))}
        </Grid>
      )}
    </Navbar>
  );
};

export default HODReportsPage;
